import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { format, parseISO } from 'date-fns';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

export const AudienceChangeImpactChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, events, currencySettings, isLoading } = useBoardChartData();
    const currencySymbol = getCurrencySymbol(currencySettings?.default);

    const chartData = useMemo(() => {
        if (!events || !finance?.dailyBreakdown) return [];

        const targetEvents = events.filter((e: any) => (e.type || e.eventType) === 'update_ad_set_target_spec');
        if (targetEvents.length === 0) return [];

        const sortedDates = Object.keys(finance.dailyBreakdown!).sort();
        const results: { name: string; cpmBefore: number; cpmAfter: number }[] = [];

        targetEvents.slice(0, 8).forEach((event: any) => {
            const eventDate = event.date?.split('T')[0];
            if (!eventDate) return;
            const dateIdx = sortedDates.indexOf(eventDate);

            const cpmForDays = (days: string[]) => {
                let totalSpend = 0, totalImpressions = 0;
                days.forEach(d => {
                    const m = finance.dailyBreakdown![d]?.metrics;
                    totalSpend += m?.adSpend || 0;
                    totalImpressions += m?.adImpressions || 0;
                });
                return totalImpressions > 0 ? (totalSpend / totalImpressions) * 1000 : 0;
            };

            const beforeDays = sortedDates.slice(Math.max(0, dateIdx - 7), dateIdx);
            const afterDays = sortedDates.slice(dateIdx, dateIdx + 7);

            const label = event.description || format(parseISO(eventDate), 'MMM dd');
            results.push({
                name: label.length > 18 ? label.substring(0, 18) + '...' : label,
                cpmBefore: Number(cpmForDays(beforeDays).toFixed(2)),
                cpmAfter: Number(cpmForDays(afterDays).toFixed(2))
            });
        });

        return results;
    }, [events, finance]);

    const isEmpty = !isLoading && chartData.length === 0;
    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={CHART_STYLES.margin.withAxis}>
                        <CartesianGrid strokeDasharray={CHART_STYLES.grid.strokeDasharray} vertical={false} stroke={CHART_STYLES.grid.stroke} opacity={CHART_STYLES.grid.opacity} />
                        <XAxis dataKey="name" {...CHART_STYLES.axis} tick={{ ...CHART_STYLES.axis.tick, fontSize: 9 }} />
                        <YAxis {...CHART_STYLES.axis} tickFormatter={(v) => formatCompactNumber(v, currencySymbol)} tick={{ ...CHART_STYLES.axis.tick, fontSize: 10 }} width={55} />
                        <Tooltip contentStyle={CHART_STYLES.tooltip.contentStyle} formatter={(v: number) => `${currencySymbol}${v.toFixed(2)}`} />
                        <Legend wrapperStyle={{ fontSize: '10px' }} />
                        <Bar dataKey="cpmBefore" name={t('charts.cpmBefore', 'CPM Before') as string} fill={CHART_COLORS.gray[400]} radius={[3, 3, 0, 0]} maxBarSize={30} />
                        <Bar dataKey="cpmAfter" name={t('charts.cpmAfter', 'CPM After') as string} fill={CHART_COLORS.chart.line5} radius={[3, 3, 0, 0]} maxBarSize={30} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
